import type { ModuleID, VersionString, ViewID } from "@bestowinc/tinkertoy-core";

import { PERFORMANCE_METRIC_NAMES, type BundleCacheMode } from "./types";
import { startPerformanceTimer } from "./timers";

function resolveLoadErrorCode(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = Reflect.get(error, "code");
    if (typeof code === "string") {
      return code;
    }
  }
  if (error instanceof Error) {
    return error.name;
  }
  return "unknown";
}

export async function trackModuleBundleLoad<T>(
  args: {
    moduleId: ModuleID;
    version: VersionString;
    cacheMode: BundleCacheMode;
  },
  load: () => Promise<T>,
): Promise<T> {
  const timer = startPerformanceTimer(PERFORMANCE_METRIC_NAMES.moduleBundleLoad, {
    moduleId: args.moduleId,
    moduleVersion: args.version,
    cacheMode: args.cacheMode,
  });

  try {
    const result = await load();
    timer.complete("success");
    return result;
  } catch (error) {
    timer.complete("failure", { errorCode: resolveLoadErrorCode(error) });
    throw error;
  }
}

export async function trackViewBundleLoad<T>(
  args: {
    viewId: ViewID;
    version: VersionString;
    cacheMode: BundleCacheMode;
  },
  load: () => Promise<T>,
): Promise<T> {
  const timer = startPerformanceTimer(PERFORMANCE_METRIC_NAMES.viewBundleLoad, {
    viewId: args.viewId,
    viewVersion: args.version,
    cacheMode: args.cacheMode,
  });

  try {
    const result = await load();
    timer.complete("success");
    return result;
  } catch (error) {
    timer.complete("failure", { errorCode: resolveLoadErrorCode(error) });
    throw error;
  }
}
